import {useEffect} from 'react';

import {
  isPanelApiError,
  type AuthSessionDto,
  type PanelAuthPort,
} from '../data/PanelDataSource.js';

/**
 * Renovação da sessão antes do vencimento por ociosidade.
 *
 * A API vence a sessão de dois jeitos (`auth-service.ts`): por ociosidade em
 * 30 minutos e por limite absoluto em 8 horas. O refresh empurra a janela de
 * ociosidade, nunca a absoluta, então o cliente renova ANTES do
 * `idleExpiresAt` e aceita que às 8 horas a sessão morre de qualquer jeito.
 *
 * O refresh GIRA o token: a sessão antiga é revogada na hora. Duas abas que
 * renovam juntas são um problema real, porque a segunda chega com o token que
 * a primeira acabou de matar e leva 401. A trava é simples e está descrita em
 * `renovarUmaVez`.
 *
 * O que vai para `localStorage` são só carimbos de tempo e o nome da aba que
 * está renovando. Token, convite e qualquer segredo NUNCA passam por aqui:
 * armazenamento local sobrevive à sessão e é legível por qualquer script da
 * origem.
 */

export const CHAVE_IDLE = 'devmil.panel.sessao.idle_expira_em';
export const CHAVE_CLAIM = 'devmil.panel.sessao.claim_de_renovacao';

/** Renova 5 minutos antes de vencer: folga para rede lenta e aba dormindo. */
export const MARGEM_DE_RENOVACAO_MS = 5 * 60 * 1000;

/** Idade máxima de um claim alheio antes de ele contar como abandonado. */
const CLAIM_FRESCO_MS = 15 * 1000;

/** Reexecução quando a renovação cedeu a vez, falhou ou ficou sem prazo. */
const NOVA_TENTATIVA_MS = 60 * 1000;

export interface KeeperStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * `localStorage` pode lançar (modo privado, cota, política). Sessão não pode
 * morrer por causa disso: sem armazenamento a trava degrada para "cada aba
 * por si", que no pior caso vira um 401 de corrida que a sonda de `me()`
 * absorve logo abaixo.
 */
const lerChave = (storage: KeeperStorage, chave: string): string | null => {
  try {
    return storage.getItem(chave);
  } catch {
    return null;
  }
};

const gravarChave = (
  storage: KeeperStorage,
  chave: string,
  valor: string,
): void => {
  try {
    storage.setItem(chave, valor);
  } catch {
    // Sem armazenamento a renovação segue funcionando, só sem coordenação.
  }
};

const removerChave = (storage: KeeperStorage, chave: string): void => {
  try {
    storage.removeItem(chave);
  } catch {
    // Idem: ausência de armazenamento não é falha de sessão.
  }
};

/** Registra o prazo da sessão recém-emitida, para as outras abas agendarem. */
export const registrarSessaoRenovada = (
  storage: KeeperStorage,
  sessao: AuthSessionDto,
): void => {
  gravarChave(storage, CHAVE_IDLE, sessao.idleExpiresAt);
};

/** No logout, os carimbos morrem junto: agendamento fantasma acorda aba morta. */
export const limparRegistroDeSessao = (storage: KeeperStorage): void => {
  removerChave(storage, CHAVE_IDLE);
  removerChave(storage, CHAVE_CLAIM);
};

/** O prazo registrado, em epoch ms. Valor ilegível vale como ausente. */
export const lerIdleExpiraEm = (storage: KeeperStorage): number | null => {
  const bruto = lerChave(storage, CHAVE_IDLE);
  if (!bruto) {
    return null;
  }
  const ms = Date.parse(bruto);
  return Number.isNaN(ms) ? null : ms;
};

export type ResultadoDaRenovacao =
  /** Esta aba girou a sessão e registrou o prazo novo. */
  | 'renovada'
  /** Outra aba está renovando agora: esta cedeu a vez. */
  | 'cedida'
  /** O refresh levou 401, mas `me()` provou sessão viva: outra aba renovou. */
  | 'mantida'
  /** Sessão morta de verdade: é hora da tela de entrada. */
  | 'perdida'
  /** Erro transitório (rede, 5xx). Tentar de novo, não deslogar. */
  | 'falhou';

/**
 * Uma tentativa de renovação, com a trava entre abas.
 *
 * A trava: um claim `aba:carimbo` em `localStorage`. Antes de renovar, a aba
 * lê o claim; claim alheio com menos de 15 segundos significa renovação em
 * andamento, e a aba cede. Senão ela grava o próprio claim e relê: se o que
 * voltou não é dela, outra aba escreveu por cima no meio, e ela cede também.
 *
 * `localStorage` não dá comparação e troca atômica, então resta uma janela de
 * milissegundos entre ler e gravar. Ela é aceitável porque o pior caso não é
 * perder a sessão: o cookie é um só por navegador, a aba que perdeu a corrida
 * leva 401 no refresh e a sonda de `me()` logo abaixo confirma que a sessão
 * continua viva com o cookie novo que a vencedora acabou de receber.
 */
export const renovarUmaVez = async (deps: {
  auth: PanelAuthPort;
  storage: KeeperStorage;
  tabId: string;
  now?: () => number;
}): Promise<ResultadoDaRenovacao> => {
  const agora = deps.now ?? Date.now;

  const claimAlheio = lerChave(deps.storage, CHAVE_CLAIM);
  if (claimAlheio) {
    const [dono, carimbo] = claimAlheio.split(':');
    const idade = agora() - Number(carimbo);
    if (
      dono !== deps.tabId &&
      Number.isFinite(idade) &&
      idade < CLAIM_FRESCO_MS
    ) {
      return 'cedida';
    }
  }

  const meuClaim = `${deps.tabId}:${String(agora())}`;
  gravarChave(deps.storage, CHAVE_CLAIM, meuClaim);
  if (lerChave(deps.storage, CHAVE_CLAIM) !== meuClaim) {
    return 'cedida';
  }

  try {
    const sessao = await deps.auth.refreshSession();
    registrarSessaoRenovada(deps.storage, sessao);
    return 'renovada';
  } catch (erro: unknown) {
    if (isPanelApiError(erro) && erro.status === 401) {
      // 401 aqui tem duas causas possíveis: sessão morta, ou outra aba girou o
      // token um instante antes. A sonda desempata; deslogar sem sondar
      // derrubaria a aba errada.
      try {
        await deps.auth.me();
        return 'mantida';
      } catch (sonda: unknown) {
        return isPanelApiError(sonda) && sonda.status === 401
          ? 'perdida'
          : 'falhou';
      }
    }
    return 'falhou';
  } finally {
    if (lerChave(deps.storage, CHAVE_CLAIM) === meuClaim) {
      removerChave(deps.storage, CHAVE_CLAIM);
    }
  }
};

/**
 * Agenda a renovação enquanto o painel autenticado está aberto.
 *
 * O prazo vem do registro que o login e cada renovação deixam no
 * armazenamento. Sem registro, a primeira renovação roda em um minuto,
 * aprende o prazo real e o registra para todas as abas.
 */
export const useSessionKeeper = (deps: {
  auth: PanelAuthPort | null;
  onSessionLost: () => void;
  storage?: KeeperStorage;
}): void => {
  const {auth, onSessionLost} = deps;
  const storage = deps.storage ?? globalThis.localStorage;

  useEffect(() => {
    if (!auth) {
      return;
    }

    const tabId = crypto.randomUUID();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let ativo = true;

    const agendar = (emMs: number) => {
      if (!ativo) {
        return;
      }
      if (timer !== null) {
        clearTimeout(timer);
      }
      timer = setTimeout(executar, Math.max(emMs, 0));
    };

    const agendarPeloPrazo = () => {
      const idle = lerIdleExpiraEm(storage);
      // Sem prazo registrado (F5 com armazenamento limpo, cookie ainda vivo),
      // a primeira renovação espera um minuto em vez de sair girando a sessão
      // na montagem: a janela de ociosidade é de 30 minutos, então não há
      // pressa, e girar já na chegada faria toda abertura do painel custar um
      // refresh.
      agendar(
        idle === null
          ? NOVA_TENTATIVA_MS
          : idle - MARGEM_DE_RENOVACAO_MS - Date.now(),
      );
    };

    const executar = () => {
      void renovarUmaVez({auth, storage, tabId}).then((resultado) => {
        if (!ativo) {
          return;
        }
        if (resultado === 'perdida') {
          onSessionLost();
          return;
        }
        if (resultado === 'renovada') {
          agendarPeloPrazo();
          return;
        }
        // Cedida, mantida ou falha transitória: o prazo pode estar sendo
        // escrito por outra aba neste instante, então a reexecução é curta e
        // relê o registro na hora de agendar.
        const idle = lerIdleExpiraEm(storage);
        const peloPrazo =
          idle === null
            ? NOVA_TENTATIVA_MS
            : idle - MARGEM_DE_RENOVACAO_MS - Date.now();
        agendar(Math.min(Math.max(peloPrazo, 0), NOVA_TENTATIVA_MS));
      });
    };

    const aoMudarStorage = (evento: StorageEvent) => {
      if (evento.key === CHAVE_IDLE) {
        agendarPeloPrazo();
      }
    };

    agendarPeloPrazo();
    globalThis.addEventListener?.('storage', aoMudarStorage);

    return () => {
      ativo = false;
      if (timer !== null) {
        clearTimeout(timer);
      }
      globalThis.removeEventListener?.('storage', aoMudarStorage);
    };
  }, [auth, onSessionLost, storage]);
};
